// Prediction Markets — Kalshi market analysis with Claude probability estimates.
// BET/PASS/FADE signals vs market prices. 4-hour cache.

import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';

interface PMResult {
  ticker:      string;
  title:       string;
  marketPrice: number;
  modelProb:   number | null;
  edge:        number | null;
  signal:      'BET' | 'PASS' | 'FADE';
  kelly:       number | null;
  reasoning:   string | null;
  closeTime:   string;
  openInterest: number;
}
interface PMResp {
  markets:  PMResult[];
  cachedAt: string;
  count:    number;
  error?:   string;
}

const SIG_COLOR = { BET: 'var(--green)', PASS: 'var(--warn)', FADE: 'var(--red)' };
const SIG_BG    = { BET: 'rgba(46,213,115,0.1)', PASS: 'rgba(255,193,7,0.1)', FADE: 'rgba(255,71,87,0.1)' };

function pctBar(value: number, baseline: number): string {
  const col = value > baseline + 0.07 ? 'var(--green)' : value < baseline - 0.07 ? 'var(--red)' : 'var(--warn)';
  const pct  = Math.round(value * 100);
  const width = Math.min(100, Math.max(2, pct));
  return `
    <div style="display:inline-flex;align-items:center;gap:6px;">
      <div style="width:52px;height:5px;background:var(--bg3);border-radius:2px;overflow:hidden;">
        <div style="width:${width}%;height:100%;background:${col};border-radius:2px;"></div>
      </div>
      <span style="font-family:var(--mono);font-size:12px;color:${col};font-weight:700;">${pct}%</span>
    </div>`;
}

function edgeBadge(edge: number): string {
  const col = edge > 0 ? 'var(--green)' : 'var(--red)';
  const sign = edge > 0 ? '+' : '';
  return `<span style="font-family:var(--mono);font-size:12px;color:${col};font-weight:700;">${sign}${Math.round(edge * 100)}%</span>`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export class PredictionMarketsTab implements Tab {
  readonly id = 'prediction-markets';
  private data: PMResp | null = null;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    document.getElementById('pm-refresh-btn')?.addEventListener('click', () => this.load(true));
    document.getElementById('pm-filter')?.addEventListener('change', () => this.renderFiltered());
  }

  render(): void {
    if (!this.data) this.load(false);
    else this.renderFiltered();
  }

  private async load(force: boolean): Promise<void> {
    const body = this.el('pm-body');
    body.innerHTML = `<div style="padding:24px;color:var(--text3);font-family:var(--mono);font-size:12px;">Fetching Kalshi markets and running probability model…</div>`;

    try {
      const data = await workerFetch<PMResp>('/api/prediction-markets', {
        method: force ? 'POST' : 'GET',
        ...(force ? { body: '{}', headers: { 'Content-Type': 'application/json' } } : {}),
        signal: AbortSignal.timeout(60000),
      });
      this.data = data;
      const updated = this.el('pm-updated');
      if (updated) updated.textContent = `Cached ${timeAgo(data.cachedAt)}`;
      this.renderFiltered();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      body.innerHTML = `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private renderFiltered(): void {
    if (!this.data) return;
    const filter = (document.getElementById('pm-filter') as HTMLSelectElement | null)?.value ?? 'all';
    let markets = this.data.markets;
    if (filter !== 'all') markets = markets.filter(m => m.signal === filter.toUpperCase());
    this.renderResults(markets);
  }

  private renderResults(markets: PMResult[]): void {
    if (!markets.length) {
      this.el('pm-body').innerHTML = `<div style="padding:24px;text-align:center;color:var(--text3);font-family:var(--mono);font-size:12px;">No markets match the current filter.</div>`;
      return;
    }

    const rows = markets.map(m => {
      const sigCol = SIG_COLOR[m.signal];
      const sigBg  = SIG_BG[m.signal];
      const hasModel = m.modelProb !== null && m.edge !== null;

      const detail = m.reasoning ? `
        <div style="padding:14px 20px 14px 24px;background:var(--bg2);border-top:1px solid var(--border);">
          <div style="font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;margin-bottom:6px;">Model Reasoning</div>
          <div style="font-size:12px;color:var(--text2);line-height:1.6;max-width:640px;">${esc(m.reasoning)}</div>
          ${m.kelly !== null ? `
          <div style="margin-top:10px;font-size:10px;color:var(--text3);">
            Kelly position size: <span style="color:${sigCol};font-family:var(--mono);font-weight:700;">${Math.round(m.kelly * 100)}%</span>
            <span style="color:var(--text3);margin-left:8px;">· ${m.signal === 'BET' ? 'Bet YES' : 'Bet NO'} on Kalshi</span>
          </div>` : ''}
        </div>` : '';

      return `
        <tr data-pm-expand="${esc(m.ticker)}" style="cursor:pointer;border-bottom:1px solid var(--border);">
          <td style="padding:11px 14px;max-width:320px;">
            <div style="font-size:12px;color:var(--text);line-height:1.4;">${esc(m.title)}</div>
            <div style="font-size:10px;color:var(--text3);margin-top:3px;font-family:var(--mono);">Expires ${fmtDate(m.closeTime)}</div>
          </td>
          <td style="padding:11px 14px;">${pctBar(m.marketPrice, 0.5)}</td>
          <td style="padding:11px 14px;">${hasModel ? pctBar(m.modelProb!, m.marketPrice) : '<span style="color:var(--text3);">—</span>'}</td>
          <td style="padding:11px 14px;">${hasModel ? edgeBadge(m.edge!) : '<span style="color:var(--text3);">—</span>'}</td>
          <td style="padding:11px 14px;">
            <span style="background:${sigBg};border:1px solid ${sigCol};color:${sigCol};font-family:var(--mono);font-size:11px;font-weight:700;padding:3px 10px;border-radius:2px;letter-spacing:0.1em;">${esc(m.signal)}</span>
          </td>
          <td style="padding:11px 14px;font-family:var(--mono);font-size:12px;color:${m.kelly ? sigCol : 'var(--text3)'};">${m.kelly !== null ? Math.round(m.kelly * 100) + '%' : '—'}</td>
          <td style="padding:11px 14px;font-size:10px;color:var(--text3);">▼</td>
        </tr>
        <tr id="pm-detail-${esc(m.ticker)}" style="display:none;">
          <td colspan="7" style="padding:0;">${detail}</td>
        </tr>`;
    }).join('');

    const betCount  = markets.filter(m => m.signal === 'BET').length;
    const fadeCount = markets.filter(m => m.signal === 'FADE').length;

    this.el('pm-body').innerHTML = `
      <div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:12px 16px;margin-bottom:16px;font-size:11px;color:var(--text3);line-height:1.6;display:flex;gap:20px;flex-wrap:wrap;align-items:center;">
        <span>Kalshi market data · Claude claude-sonnet-4-6 probability model · Platt-calibrated · 4-hour cache</span>
        <span style="margin-left:auto;display:flex;gap:14px;">
          <span><span style="color:var(--green);font-weight:700;">BET</span> ${betCount}</span>
          <span><span style="color:var(--red);font-weight:700;">FADE</span> ${fadeCount}</span>
          <span><span style="color:var(--warn);font-weight:700;">PASS</span> ${markets.length - betCount - fadeCount}</span>
        </span>
      </div>
      <div style="overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;min-width:680px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border);">
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Market</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Kalshi Price</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Model Prob</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Edge</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;">Signal</th>
            <th style="padding:7px 14px;text-align:left;font-size:10px;color:var(--text3);text-transform:uppercase;letter-spacing:0.1em;" title="Kelly Criterion sizing">Kelly</th>
            <th style="padding:7px 14px;"></th>
          </tr>
        </thead>
        <tbody id="pm-rows">${rows}</tbody>
      </table>
      </div>
      <div style="font-size:10px;color:var(--text3);text-align:right;padding:8px 2px;margin-top:4px;">
        Click row to expand model reasoning · BET ≥+7% edge · FADE ≤−7% edge · Kelly = recommended position fraction
      </div>`;

    this.el('pm-body').addEventListener('click', (e: MouseEvent) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('[data-pm-expand]');
      if (row) {
        const detail = document.getElementById('pm-detail-' + row.dataset.pmExpand);
        if (detail) detail.style.display = detail.style.display === 'none' ? '' : 'none';
      }
    });
  }
}
